import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@ai-tutor/ui';
import { PracticeRunner } from '@/components/features/mindspace/practice/runners/PracticeRunner';
import { usePracticeSession } from '@/hooks/mindspace/usePracticeSession';
import {
  isReviewScope,
  type SessionConfig,
  type SessionOutcome,
} from '@/lib/practice/session-types';

/**
 * Quiz path - thin wrapper around `PracticeRunner` that owns just the
 * queue fetch + the client-side conceptIds filter. The runner itself
 * renders the shared `RunnerTopBar` so it's a structural peer of
 * `FlashcardRunner`.
 */
export function QuizSessionShell({
  config,
  onEnd,
  onAbort,
  onProgress,
}: {
  config: SessionConfig;
  onEnd: (outcomes: SessionOutcome[], total?: number) => void;
  onAbort: () => void;
  onProgress?: (outcomes: SessionOutcome[], total: number) => void;
}) {
  // Shared queue + filtering wiring (same hook FlashcardRunner uses).
  // Mode='quiz' tells the hook to drop concepts whose `questions` is
  // empty - the runner needs LLM output to render an MCQ, unlike
  // flashcards which fall back to the concept's own name/description.
  const {
    rawConcepts: allConcepts,
    concepts,
    isLoading,
    isFetching,
    refetch,
  } = usePracticeSession(config, 'quiz');
  // Optimistic retry state - `refetch()` is async and RQ's `isFetching`
  // doesn't flip to true synchronously, so without this the stale
  // "Couldn't generate quiz questions" message lingers for a render
  // tick after the user clicks Retry. Toggling `retrying` instantly
  // swaps the error UI for the loading spinner.
  const [retrying, setRetrying] = useState(false);
  useEffect(() => {
    // Reset once RQ confirms the refetch is in-flight or done - we
    // only need the optimistic flip to cover the single-frame gap.
    if (retrying && !isFetching) {
      setRetrying(false);
    }
  }, [retrying, isFetching]);

  if (isLoading || retrying) {
    return (
      <div className='flex h-full flex-col items-center justify-center gap-3 py-16'>
        <Loader2 className='h-6 w-6 animate-spin text-brand-600 dark:text-primary' />
        <p className='text-sm text-muted-foreground'>Loading concepts…</p>
      </div>
    );
  }

  // Distinguish "no concepts at all" from "concepts exist but the LLM
  // produced no questions" - the quiz literally needs the LLM output
  // to function, so honesty about which case we're in matters.
  // The backend now guarantees at least a stock question per concept,
  // so this state should be unreachable in normal operation. Retry
  // is the obvious recovery for the rare transport-layer failure.
  if (allConcepts.length > 0 && concepts.length === 0) {
    return (
      <div className='flex h-full flex-col items-center justify-center gap-3 py-16 text-center'>
        <p className='text-sm font-medium text-foreground'>
          Couldn&apos;t generate quiz questions for these concepts.
        </p>
        <p className='max-w-md text-xs text-muted-foreground'>
          Try again, or switch to Flashcards mode.
        </p>
        <div className='mt-2 flex items-center gap-2'>
          <Button
            onClick={() => {
              // Flip `retrying` immediately so the next render shows
              // the loading state, then kick off the actual refetch.
              setRetrying(true);
              void refetch();
            }}
            disabled={isFetching || retrying}
            className='gap-2'
          >
            {(isFetching || retrying) && (
              <Loader2 className='h-3.5 w-3.5 animate-spin' />
            )}
            Retry
          </Button>
          <Button variant='outline' onClick={onAbort}>
            Back to setup
          </Button>
        </div>
      </div>
    );
  }

  return (
    <PracticeRunner
      concepts={concepts}
      isLoading={isLoading}
      onEnd={(outcomes, t) => onEnd(outcomes, t)}
      onAbort={onAbort}
      onProgress={onProgress}
      // Only Review scopes (Due / Weak) submit FSRS reviews; Practice
      // scopes drill no-stakes.
      recordReview={isReviewScope(config.scope)}
    />
  );
}
